
import React from 'react';
import { cn } from '@/lib/utils';
import { motion } from "framer-motion";
import { useAnimationSimplifier } from "@/hooks/use-animation-simplifier";
import { useIsMobile } from "@/hooks/use-mobile";

interface CircleDecorProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'accent';
  animated?: boolean;
}

export const CircleDecor: React.FC<CircleDecorProps> = ({ 
  className, 
  size = 'md', 
  variant = 'primary',
  animated = false
}) => {
  const { shouldSimplifyAnimations, animationDuration } = useAnimationSimplifier();
  
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const variantClasses = {
    primary: 'bg-gradient-to-br from-blue-400 to-purple-500',
    secondary: 'bg-gradient-to-br from-blue-300 to-blue-500', // Changed from pink-400 to-orange-500 to blue tones
    accent: 'bg-gradient-to-br from-green-400 to-blue-500'
  };

  return (
    <motion.div 
      className={cn(
        'rounded-full blur-xl opacity-20',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      animate={animated && !shouldSimplifyAnimations ? {
        y: [0, -10, 0],
        scale: [1, 1.05, 1],
      } : undefined}
      transition={animated && !shouldSimplifyAnimations ? {
        duration: animationDuration * 5,
        repeat: Infinity,
        ease: "easeInOut"
      } : undefined}
    />
  );
};

interface FloatingCardProps {
  className?: string;
  children?: React.ReactNode;
  delay?: number;
}

export const FloatingCard = ({ className, children, delay = 0 }: FloatingCardProps) => {
  const { shouldSimplifyAnimations, animationDuration } = useAnimationSimplifier();
  
  return (
    <motion.div
      className={cn(
        "bg-white/40 backdrop-blur-sm border border-brand-blue/10 rounded-lg shadow-md p-4 md:p-6 transform-gpu",
        className
      )}
      initial={{ y: 20, opacity: 0 }}
      animate={shouldSimplifyAnimations ? { 
        opacity: 1,
        y: 0 
      } : { 
        y: [0, -10, 0],
        opacity: 1,
      }}
      transition={{
        y: {
          delay: delay,
          duration: shouldSimplifyAnimations ? animationDuration : 4,
          repeat: shouldSimplifyAnimations ? 0 : Infinity,
          ease: "easeInOut",
        },
        opacity: {
          duration: animationDuration,
        }
      }}
    >
      {children}
    </motion.div>
  );
};

interface WaveDecorProps {
  className?: string;
  variant?: 'primary' | 'secondary';
}

export const WaveDecor = ({ className, variant = 'primary' }: WaveDecorProps) => {
  return (
    <div className={cn("absolute w-full overflow-hidden", className)}>
      <svg 
        className={`w-full ${variant === 'primary' ? 'text-brand-blue/10' : 'text-brand-silver/20'}`} 
        viewBox="0 0 1440 120" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          d="M0 120L48 110C96 100 192 80 288 75C384 70 480 80 576 85C672 90 768 90 864 85C960 80 1056 70 1152 65C1248 60 1344 60 1392 60L1440 60V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V120Z" 
          fill="currentColor"
        />
      </svg>
    </div>
  );
};

interface Card3DProps {
  className?: string;
  children?: React.ReactNode;
  depth?: number;
  disabled?: boolean;
}

export const Card3D = ({ className, children, depth = 5, disabled = false }: Card3DProps) => {
  const isMobile = useIsMobile();
  const { shouldSimplifyAnimations } = useAnimationSimplifier();
  
  // Disable 3D effects on mobile devices or when explicitly disabled
  const shouldApply3DEffects = !isMobile && !disabled;
  
  return (
    <div className={shouldApply3DEffects ? "perspective-1000" : ""}>
      <motion.div
        className={cn(
          "bg-white/90 backdrop-blur-sm border border-brand-blue/10 rounded-lg shadow-md",
          className
        )}
        whileHover={
          shouldApply3DEffects && !shouldSimplifyAnimations
            ? {
                rotateY: 5,
                rotateX: -5,
                translateZ: depth,
                transition: { duration: 0.3 }
              }
            : { 
                scale: 1.02,
                boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.1)",
                transition: { duration: 0.3 }
              }
        }
      >
        {children}
      </motion.div>
    </div>
  );
};

interface IllustrationProps {
  className?: string;
  children?: React.ReactNode;
  type?: 'meditation' | 'conversation' | 'insights';
  animated?: boolean;
}

export const Illustration = ({ className, children, type, animated = false }: IllustrationProps) => {
  return (
    <div className={cn("relative", className)}>
      {children}
    </div>
  );
};
